import { styled } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import cn from 'classnames';
import { forwardRef, useMemo } from 'react';
import { InviteStatus } from 'src/provider/VoiceChannel';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { CPAvatarSize, avatarSizes } from '../base/CPAvatar/avatarSizes';
import { CustomAvatar, CustomAvatarProps } from '../custom-avatar';
import { CallingAvatar } from './CallingAvatar/CallingAvatar';

interface Props extends CustomAvatarProps {
  meetId?: string;
  identity?: string;
  inviteStatus?: InviteStatus;
  size: CPAvatarSize;
}

export const SpeakingAvatar = forwardRef<HTMLDivElement, Props>(
  ({ meetId, identity, inviteStatus, size = 'medium', ...rest }, ref) => {
    const theme = useTheme();
    const { room, myMeetId, connectionStatus } = useLivekitRTCState();

    const connected = useMemo(
      () => connectionStatus !== 'disconnected',
      [connectionStatus],
    );

    const participant = useMemo(() => {
      if (!room || !connected || !meetId || !identity) return undefined;

      return meetId === myMeetId
        ? room.localParticipant
        : room.remoteParticipants.get(identity);
    }, [room, connected, meetId, identity, myMeetId]);

    const isSpeaking = useMemo(() => {
      if (inviteStatus === 'calling') return true;
      return participant?.isSpeaking || false;
    }, [participant?.isSpeaking, inviteStatus]);

    return (
      <CallingAvatar ref={ref} inviteStatus={inviteStatus} size={size}>
        <StyledCustomAvatar
          className={cn({ isSpeaking })}
          sx={{
            ...avatarSizes[size],
            clipPath: undefined,
          }}
          {...rest}
        />
      </CallingAvatar>
    );
  },
);

const StyledCustomAvatar = styled(CustomAvatar)(({ theme }) => ({
  zIndex: 1,
  '--box-speaking-color': alpha(theme.palette.success.main, 0.5),
  '&.isSpeaking': {
    boxShadow: `-1.3px -1.3px 0 0 var(--box-speaking-color),
                1.3px 1.3px 0 0 var(--box-speaking-color),
                -1.3px 1.3px 0 0 var(--box-speaking-color),
                1.3px -1.3px 0 0 var(--box-speaking-color),
                -2px 0 0 0 var(--box-speaking-color),
                2px 0 0 0 var(--box-speaking-color),
                0 2px 0 0 var(--box-speaking-color),
                0 -2px 0 0 var(--box-speaking-color)`,
  },
}));
